import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { API_CONFIG, logApiCall, logApiError } from '@/config/api.config';

/**
 * Base Query Configuration for Odoo Backend
 * 
 * Odoo API uses POST requests with query parameters (e.g., ?lang=ar_IQ).
 * This custom base query handles:
 * - CORS headers
 * - JSON requests/responses
 * - Error logging
 */

/**
 * Custom fetch function for API logging
 */
const customFetchFn: typeof fetch = async (input, init) => {
  // Log the API call in development
  if (API_CONFIG.debug && typeof input === 'string') {
    logApiCall(init?.method || 'GET', input, init?.body);
  }

  try {
    const response = await fetch(input, init);
    return response;
  } catch (error) {
    logApiError(error);
    throw error;
  }
};

/**
 * Real API Base Query for Odoo Backend
 * 
 * Wrapped to prevent infinite retries on errors
 */
const baseQueryWithErrorHandling: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await fetchBaseQuery({
    baseUrl: API_CONFIG.baseUrl,
    fetchFn: customFetchFn,
    prepareHeaders: (headers) => {
      // Only set Content-Type if there's a body (to avoid CORS preflight for empty POST requests)
      // The backend reads from query parameters, not request body
      // Accept header is always set
      headers.set('Accept', 'application/json');
      
      return headers;
    },
    timeout: API_CONFIG.timeout,
  })(args, api, extraOptions);

  // Don't retry on errors - return immediately
  // This prevents infinite retry loops when backend has transaction errors
  return result;
};

export const baseQuery = baseQueryWithErrorHandling;

/**
 * Development Mode: Uncomment to use mock data instead of real API
*/
// import { fakeBaseQuery } from '@reduxjs/toolkit/query/react';
// export const baseQuery = fakeBaseQuery() as BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;
